(() => {
  const registry = window.PolyPouchSearchIntentRegistry?.rules || {};
  const config = window.PolyPouchSearchIntentConfig || {};
  const pendingStorageKey = 'polypouch_search_intent_pending';
  const pendingAnalyticsStorageKey = 'polypouch_search_intent_analytics_pending';
  const pendingAnalyticsMaxAge = 60000;

  const normalize = (value) => String(value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

  const getRule = (value) => registry[normalize(value)] || null;

  const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"]/g, (character) => {
      const entities = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' };
      return entities[character];
    });

  const createEvent = (rule, searchTerm, surface, destination = rule.destination || null) => ({
    event: 'search_intent_rule_applied',
    search_term: String(searchTerm ?? ''),
    normalized_term: normalize(searchTerm),
    action: rule.action,
    destination,
    surface,
    message_type: rule.messageType || null,
  });

  const pushEventPayload = (payload) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  };

  const pushEvent = (rule, searchTerm, surface, destination = rule.destination || null) => {
    pushEventPayload(createEvent(rule, searchTerm, surface, destination));
  };

  const storePendingEvent = (rule, searchTerm, surface, destination = rule.destination || null) => {
    const payload = createEvent(rule, searchTerm, surface, destination);
    try {
      window.sessionStorage.setItem(
        pendingAnalyticsStorageKey,
        JSON.stringify({ payload, createdAt: Date.now() })
      );
      return true;
    } catch (error) {
      pushEventPayload(payload);
      return false;
    }
  };

  const flushPendingEvent = () => {
    try {
      const storedValue = window.sessionStorage.getItem(pendingAnalyticsStorageKey);
      if (!storedValue) return;
      window.sessionStorage.removeItem(pendingAnalyticsStorageKey);
      const pending = JSON.parse(storedValue);
      if (pending.payload && Date.now() - pending.createdAt < pendingAnalyticsMaxAge) {
        pushEventPayload(pending.payload);
      }
    } catch (error) {
      return;
    }
  };

  const storePendingSubmission = (searchTerm) => {
    try {
      window.sessionStorage.setItem(
        pendingStorageKey,
        JSON.stringify({ term: normalize(searchTerm), createdAt: Date.now() })
      );
    } catch (error) {
      return;
    }
  };

  const consumePendingSubmission = (searchTerm) => {
    try {
      const storedValue = window.sessionStorage.getItem(pendingStorageKey);
      if (!storedValue) return false;
      window.sessionStorage.removeItem(pendingStorageKey);
      const pending = JSON.parse(storedValue);
      return pending.term === normalize(searchTerm) && Date.now() - pending.createdAt < 15000;
    } catch (error) {
      return false;
    }
  };

  const isSearchForm = (form) => {
    if (!(form instanceof HTMLFormElement)) return false;
    const action = new URL(form.action, window.location.origin);
    return action.origin === window.location.origin && action.pathname === config.searchUrl;
  };

  const getFormTerm = (form) => new FormData(form).get('q') || '';

  const isRedirectRule = (rule) => rule?.action.endsWith('_redirect');

  const navigate = (destination, replace = false) => {
    const url = new URL(destination, window.location.origin);
    if (replace) {
      window.location.replace(url.href);
    } else {
      window.location.assign(url.href);
    }
  };

  const renderDestinationPrediction = (rule, searchTerm) => {
    const destination = escapeHtml(rule.destination);
    const label = escapeHtml(rule.label);
    const encodedTerm = escapeHtml(searchTerm);
    return `
      <div id="predictive-search-results" role="listbox">
        <div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-products" data-predictive-search-groups>
          <div class="predictive-search__result-group">
            <h2 id="predictive-search-intent-heading" class="predictive-search__heading text-body caption-with-letter-spacing">${escapeHtml(config.suggestedDestination)}</h2>
            <ul class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-intent-heading">
              <li id="predictive-search-option-intent" class="predictive-search__list-item" role="option" aria-selected="false">
                <a href="${destination}" class="predictive-search__item link link--text" tabindex="-1" data-search-intent-link data-search-intent-term="${encodedTerm}">
                  <div class="predictive-search__item-content predictive-search__item-content--centered">
                    <p class="predictive-search__item-heading h5">${label}</p>
                  </div>
                </a>
              </li>
            </ul>
          </div>
        </div>
        <span class="visually-hidden" data-predictive-search-live-region-count-value>${escapeHtml(config.oneSuggestedResult)}</span>
      </div>`;
  };

  const renderMessagePrediction = (rule, searchTerm) => {
    const message = config.messages?.[rule.messageType] || '';
    const encodedTerm = escapeHtml(searchTerm);
    const actions =
      rule.messageType === 'unsupported_product'
        ? `<ul class="search-intent-predictive__actions list-unstyled" role="group" aria-label="${escapeHtml(config.availableActions)}">
            <li id="predictive-search-option-contact" class="predictive-search__list-item" role="option" aria-selected="false">
              <a href="${escapeHtml(config.contactUrl)}" class="predictive-search__item link link--text" tabindex="-1" data-search-intent-link data-search-intent-term="${encodedTerm}">${escapeHtml(config.contactLabel)}</a>
            </li>
            <li id="predictive-search-option-all" class="predictive-search__list-item" role="option" aria-selected="false">
              <a href="${escapeHtml(config.allPouchesUrl)}" class="predictive-search__item link link--text" tabindex="-1" data-search-intent-link data-search-intent-term="${encodedTerm}">${escapeHtml(config.allPouchesLabel)}</a>
            </li>
          </ul>`
        : '';
    return `
      <div id="predictive-search-results" role="listbox">
        <div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-products" data-predictive-search-groups>
          <div class="predictive-search__result-group search-intent-predictive">
            <h2 id="predictive-search-intent-heading" class="predictive-search__heading text-body caption-with-letter-spacing">${escapeHtml(config.guidanceHeading)}</h2>
            <p class="search-intent-predictive__message">${escapeHtml(message)}</p>
            ${actions}
          </div>
        </div>
        <span class="visually-hidden" data-predictive-search-live-region-count-value>${escapeHtml(config.guidanceAvailable)}</span>
      </div>`;
  };

  const renderPredictive = (rule, searchTerm) =>
    rule.action === 'message'
      ? renderMessagePrediction(rule, searchTerm)
      : renderDestinationPrediction(rule, searchTerm);

  const releasePendingState = () => document.documentElement.classList.remove('search-intent-pending');

  const showMessageState = (rule) => {
    const container = document.querySelector('[data-search-intent-message]');
    const searchTemplate = document.querySelector('.template-search');
    if (!container || !searchTemplate) {
      releasePendingState();
      return;
    }

    const message = container.querySelector('[data-search-intent-message-text]');
    const actions = container.querySelector('[data-search-intent-message-actions]');
    message.textContent = config.messages?.[rule.messageType] || '';
    actions.hidden = rule.messageType !== 'unsupported_product';
    container.hidden = false;
    searchTemplate.classList.add('template-search--intent-message');
    releasePendingState();
  };

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!isSearchForm(form) || form.querySelector('[aria-selected="true"] a')) return;
      const searchTerm = getFormTerm(form);
      const rule = getRule(searchTerm);
      if (!rule) return;

      if (isRedirectRule(rule)) {
        event.preventDefault();
        event.stopPropagation();
        storePendingSubmission(searchTerm);
        storePendingEvent(rule, searchTerm, 'submit');
        navigate(rule.destination);
        return;
      }

      storePendingSubmission(searchTerm);
      storePendingEvent(rule, searchTerm, 'submit');
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const link = event.target.closest('[data-search-intent-link]');
      if (!link) return;
      const searchTerm = link.dataset.searchIntentTerm || '';
      const rule = getRule(searchTerm);
      if (!rule) return;
      event.preventDefault();
      event.stopPropagation();
      const destination = new URL(link.href, window.location.origin);
      storePendingEvent(rule, searchTerm, 'predictive', `${destination.pathname}${destination.search}`);
      navigate(destination.href);
    },
    true
  );

  const handleDirectSearch = () => {
    if (window.location.pathname !== config.searchUrl) {
      releasePendingState();
      return;
    }

    const searchTerm = new URL(window.location.href).searchParams.get('q') || '';
    const rule = getRule(searchTerm);
    const submitted = consumePendingSubmission(searchTerm);

    if (!rule) {
      releasePendingState();
      return;
    }

    if (isRedirectRule(rule)) {
      if (!submitted) storePendingEvent(rule, searchTerm, 'direct_url');
      navigate(rule.destination, true);
      return;
    }

    if (!submitted) pushEvent(rule, searchTerm, 'direct_url');

    if (rule.action === 'message') {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => showMessageState(rule), { once: true });
      } else {
        showMessageState(rule);
      }
      return;
    }

    releasePendingState();
  };

  window.PolyPouchSearchIntent = Object.freeze({ getRule, normalize, renderPredictive });
  flushPendingEvent();
  handleDirectSearch();
})();
